// How the SEGMENT row sends Pipehouse's Segment calls, and everything its CDP modes need that
// runs without a pipe: the mode a row asks for, the visitor as analytics.js keeps it, the
// copy of a call as Segment receives it, and the CDP client set up for those copies. The
// rules and what each one protects are in docs/architecture/segment-delivery.md.
//
// Only type imports: the co-located test runs under bare `node --test`. The client is
// loaded with `import()` when the first copy is sent.

import type { Analytics, CallOptions } from '@cppv3/cdp-browser'

/** Mirrors SEGMENT_DELIVERY_MODES in api/src/modules/integrations/dto/integrations.types.ts. */
export const SEGMENT_DELIVERY_MODES = ['segment', 'shadow', 'cdp'] as const
export type SegmentDelivery = (typeof SEGMENT_DELIVERY_MODES)[number]

/** The row's config, validated by `SegmentIntegrationConfigDto` on the API. */
export interface SegmentConfig {
  writeKey?: string
  /** Absent → `segment`: the page behaves exactly as it did before the setting existed. */
  delivery?: SegmentDelivery
  /** The CDP source imported from this Segment source, never the funnel's own source. */
  cdpWriteKey?: string
  /** Edge origin override. Absent → the edge of the build's `APP_ENV`. */
  cdpHost?: string
}

/**
 * How a page sends, from the row alone, so getHead() and setup() can never disagree. A CDP
 * mode without a CDP write key is Segment: a row missing its key keeps sending to Segment
 * rather than going silent.
 */
export function deliveryOf(config: SegmentConfig | null | undefined): SegmentDelivery {
  const delivery = config?.delivery
  if (delivery !== 'shadow' && delivery !== 'cdp') return 'segment'
  return config?.cdpWriteKey?.trim() ? delivery : 'segment'
}

/** Who delivers an event, per event, so a release can switch while others have not. */
export type DeliveryOwner = 'segment' | 'cdp'

// ---------------------------------------------------------------------------
// The visitor, kept where analytics.js 1.84.3 keeps it

/** The page's storage and cookies, as the visitor is kept in them. A page passes `window`. */
export interface VisitorWeb {
  document?: { cookie: string }
  localStorage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  location?: { hostname: string }
}

export interface Visitor {
  anonymousId: string | null
  userId: string | null
  traits: Record<string, unknown>
}

const AJS_ANONYMOUS_ID = 'ajs_anonymous_id'
const AJS_USER_ID = 'ajs_user_id'
const AJS_USER_TRAITS = 'ajs_user_traits'
const AJS_GROUP_ID = 'ajs_group_id'
const AJS_GROUP_PROPERTIES = 'ajs_group_properties'
const COOKIE_DAYS = 365
const DAY_MS = 864e5

function storageOf(web: VisitorWeb): VisitorWeb['localStorage'] {
  try {
    return web.localStorage
  } catch {
    return undefined
  }
}

/** analytics.js's LocalStorage store: JSON, or the raw text an older writer left. */
function localValue(web: VisitorWeb, key: string): unknown {
  try {
    const raw = storageOf(web)?.getItem(key) ?? null
    if (raw === null) return null
    try {
      return JSON.parse(raw) ?? null
    } catch {
      return raw
    }
  } catch {
    return null
  }
}

/** analytics.js's CookieStorage store: an empty cookie is none. */
function cookieValue(web: VisitorWeb, key: string): unknown {
  const raw = readCookie(web, key)
  if (!raw) return null
  try {
    return JSON.parse(raw) ?? null
  } catch {
    return raw
  }
}

/** An identifier as analytics.js hands it back: a non-empty string, a number as its digits. */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return String(value)
  return typeof value === 'string' && value !== '' ? value : null
}

/** The anonymous id: the cookie wins a disagreement, because only it reaches every subdomain. */
export function storedAnonymousId(web: VisitorWeb): string | null {
  return asId(cookieValue(web, AJS_ANONYMOUS_ID)) ?? asId(localValue(web, AJS_ANONYMOUS_ID))
}

/** The user id: localStorage first, and an empty value there hides the cookie, as in analytics.js. */
function storedUserIdValue(web: VisitorWeb): unknown {
  const local = localValue(web, AJS_USER_ID)
  return local !== null ? local : cookieValue(web, AJS_USER_ID)
}

export function storedUserId(web: VisitorWeb): string | null {
  return asId(storedUserIdValue(web))
}

function storedTraits(web: VisitorWeb): Record<string, unknown> {
  const traits = localValue(web, AJS_USER_TRAITS)
  return traits && typeof traits === 'object' && !Array.isArray(traits)
    ? (traits as Record<string, unknown>)
    : {}
}

/** Side by side: the visitor analytics.js will put on a call, read and never written. */
export function readVisitor(web: VisitorWeb): Visitor {
  return {
    anonymousId: storedAnonymousId(web),
    userId: storedUserId(web),
    traits: storedTraits(web),
  }
}

/**
 * The visitor an identify leaves, by analytics.js's rule: the new traits go over the stored ones
 * unless the call names a different user, and an empty user id changes nobody.
 */
export function identifiedVisitor(
  visitor: Visitor,
  userId: string,
  traits: Record<string, unknown>,
): Visitor {
  const sameUser = visitor.userId === null || visitor.userId === userId
  return {
    anonymousId: visitor.anonymousId,
    userId: userId || visitor.userId,
    traits: sameUser ? { ...visitor.traits, ...traits } : { ...traits },
  }
}

/**
 * CDP mode: the anonymous id read as analytics.js reads it for an event, minted when there is
 * none, and written back to every store, which renews the cookie for a year on each call.
 */
export function keepAnonymousId(web: VisitorWeb, uuid: () => string, now: number): string {
  const id = storedAnonymousId(web) ?? uuid()
  writeAjs(web, AJS_ANONYMOUS_ID, id, true, now)
  return id
}

/** CDP mode: the user id read for an event, and written back as analytics.js writes it. */
export function keepUserId(web: VisitorWeb, now: number): string | null {
  const value = storedUserIdValue(web)
  if (value === null) return null
  writeAjs(web, AJS_USER_ID, typeof value === 'number' ? String(value) : value, true, now)
  return asId(value)
}

/** CDP mode: what analytics.js's identify stores. Naming a different user starts a new visitor. */
export function keepIdentify(
  web: VisitorWeb,
  userId: string,
  traits: Record<string, unknown>,
  now: number,
): { userId: string | null; traits: Record<string, unknown> } {
  const currentId = keepUserId(web, now)
  const merged =
    currentId === null || currentId === userId ? { ...storedTraits(web), ...traits } : { ...traits }
  if (userId) {
    writeAjs(web, AJS_USER_ID, userId, true, now)
    if (currentId !== null && currentId !== userId) writeAjs(web, AJS_ANONYMOUS_ID, null, true, now)
  }
  writeAjs(web, AJS_USER_TRAITS, merged, false, now)
  return { userId: userId || currentId, traits: merged }
}

/** CDP mode: analytics.js's reset() and then setAnonymousId(), which is what `user:reset` ran. */
export function resetVisitor(web: VisitorWeb, uuid: () => string, now: number): void {
  writeAjs(web, AJS_USER_ID, null, true, now)
  writeAjs(web, AJS_ANONYMOUS_ID, null, true, now)
  writeAjs(web, AJS_USER_TRAITS, null, false, now)
  writeAjs(web, AJS_GROUP_ID, null, true, now)
  writeAjs(web, AJS_GROUP_PROPERTIES, null, false, now)
  writeAjs(web, AJS_ANONYMOUS_ID, uuid(), true, now)
}

/**
 * Every store a key lives in, as analytics.js writes it: JSON in localStorage, a string as itself
 * in the cookie (a year, the top-level domain, `/`, SameSite=Lax). `null` removes it.
 */
function writeAjs(
  web: VisitorWeb,
  key: string,
  value: unknown,
  cookie: boolean,
  now: number,
): void {
  try {
    const storage = storageOf(web)
    if (value === null) storage?.removeItem(key)
    else storage?.setItem(key, JSON.stringify(value))
  } catch {
    // Full or blocked: the cookie still carries it.
  }
  if (!cookie) return
  const domain = topLevelDomain(web, now)
  const attributes = {
    path: '/',
    sameSite: 'Lax',
    expires: new Date(value === null ? now - DAY_MS : now + COOKIE_DAYS * DAY_MS).toUTCString(),
    domain,
  }
  writeCookie(
    web,
    key,
    value === null ? '' : typeof value === 'string' ? value : JSON.stringify(value),
    attributes,
  )
}

const tldOf = new WeakMap<object, string | undefined>()

/** The shortest suffix of the host a cookie can be set on, probed as analytics.js probes it. */
export function topLevelDomain(web: VisitorWeb, now: number): string | undefined {
  if (tldOf.has(web)) return tldOf.get(web)
  let found: string | undefined
  const host = web.location?.hostname ?? ''
  const parts = host.split('.')
  const isIp = parts.length === 4 && Number.parseInt(parts[3] ?? '', 10) > 0
  if (!isIp && parts.length > 1) {
    for (let index = parts.length - 2; index >= 0; index -= 1) {
      const domain = parts.slice(index).join('.')
      try {
        writeCookie(web, '__tld__', '1', { domain: `.${domain}` })
        if (readCookie(web, '__tld__')) {
          writeCookie(web, '__tld__', '', {
            domain: `.${domain}`,
            expires: new Date(now - DAY_MS).toUTCString(),
          })
          found = domain
          break
        }
      } catch {
        break
      }
    }
  }
  tldOf.set(web, found)
  return found
}

/** js-cookie 3's get: the first cookie of that name, unquoted, its `%XX` runs decoded. */
function readCookie(web: VisitorWeb, name: string): string | undefined {
  let jar: string | undefined
  try {
    jar = web.document?.cookie
  } catch {
    return undefined
  }
  if (!jar) return undefined
  for (const cookie of jar.split('; ')) {
    const parts = cookie.split('=')
    const value = parts.slice(1).join('=')
    try {
      if (decodeURIComponent(parts[0] ?? '') === name) {
        const unquoted = value.startsWith('"') ? value.slice(1, -1) : value
        return unquoted.replace(/(%[\dA-F]{2})+/gi, (encoded) => decodeURIComponent(encoded))
      }
    } catch {
      // One that does not decode is passed over.
    }
  }
  return undefined
}

/** js-cookie 3's set: name and value percent-encoded but for what it leaves readable. */
function writeCookie(
  web: VisitorWeb,
  name: string,
  value: string,
  attributes: Record<string, string | undefined>,
): void {
  const document = web.document
  if (!document) return
  const encodedName = encodeURIComponent(name)
    .replace(/%(2[346B]|5E|60|7C)/g, (encoded) => decodeURIComponent(encoded))
    .replace(/[()]/g, (bracket) => (bracket === '(' ? '%28' : '%29'))
  const encodedValue = encodeURIComponent(value).replace(
    /%(2[346BF]|3[AC-F]|40|5[BDE]|60|7[BCD])/g,
    (encoded) => decodeURIComponent(encoded),
  )
  let text = `${encodedName}=${encodedValue}`
  for (const [attribute, setting] of Object.entries({ path: '/', ...attributes })) {
    if (setting) text += `; ${attribute}=${setting.split(';')[0] ?? ''}`
  }
  document.cookie = text
}

// ---------------------------------------------------------------------------
// The copy of a call, as Segment receives it

/** The page as analytics.js's snippet captures it at the call (`__t: 'bpc'`). */
export interface BufferedPage {
  canonical?: string
  path: string
  url: string
  search: string
  title: string
  referrer: string
}

export interface PageWeb {
  document?: {
    title: string
    referrer: string
    querySelector?: (selector: string) => { getAttribute(name: string): string | null } | null
  }
  location?: { href: string; pathname: string; search: string }
}

export function bufferedPage(web: PageWeb): BufferedPage {
  const canonical = web.document?.querySelector?.("link[rel='canonical']")?.getAttribute('href')
  return {
    ...(canonical ? { canonical } : {}),
    path: web.location?.pathname ?? '',
    url: web.location?.href ?? '',
    search: web.location?.search ?? '',
    title: web.document?.title ?? '',
    referrer: web.document?.referrer ?? '',
  }
}

export interface PageContext {
  path: string
  referrer: string
  search: string
  title: string
  url: string
}

const PAGE_CONTEXT_KEYS = ['path', 'referrer', 'search', 'title', 'url'] as const

/** `createPageContext` of @segment/analytics-page-tools: the canonical link wins, the #fragment goes. */
export function pageContextOf(page: BufferedPage): PageContext {
  const canonical = page.canonical
  let path = page.path
  let url = page.url
  if (canonical) {
    try {
      path = new URL(canonical).pathname
    } catch {
      path = canonical[0] === '/' ? canonical : `/${canonical}`
    }
    url = canonical.includes('?') ? canonical : canonical + page.search
  } else {
    const hash = url.indexOf('#')
    if (hash !== -1) url = url.slice(0, hash)
  }
  return { path, referrer: page.referrer, search: page.search, title: page.title, url }
}

/** analytics.js's `utm()`, oddities kept: `?` splits like `&`, `utm_campaign` is `name`. */
export function campaignOf(query: string): Record<string, string> {
  const rest = (query.startsWith('?') ? query.substring(1) : query).replace(/\?/g, '&')
  const campaign: Record<string, string> = {}
  for (const part of rest.split('&')) {
    const [key = '', value = ''] = part.split('=')
    if (key.includes('utm_') && key.length > 4) {
      const name = key.slice(4)
      campaign[name === 'campaign' ? 'name' : name] = gracefulDecode(value)
    }
  }
  return campaign
}

function gracefulDecode(encoded: string): string {
  try {
    return decodeURIComponent(encoded.replace(/\+/g, ' '))
  } catch {
    return encoded
  }
}

/** One call as Pipehouse makes it to analytics.js. */
export type SegmentCall =
  | { type: 'track'; event: string; properties: Record<string, unknown> }
  | { type: 'page'; name?: string; properties: Record<string, unknown> }
  | { type: 'identify'; userId: string; traits: Record<string, unknown> }

/** The CDP event for one call, but for what the client adds: user agent, hints, locale, library. */
export interface CdpCall {
  type: SegmentCall['type']
  event?: string
  name?: string
  properties: Record<string, unknown>
  traits?: Record<string, unknown>
  messageId: string
  anonymousId: string
  userId?: string
  originalTimestamp: string
  context: {
    page: PageContext
    campaign?: Record<string, string>
    timezone?: string
    deliveryOwner: DeliveryOwner
  }
}

export interface CallMoment {
  messageId: string
  /** When the call was made, epoch milliseconds. */
  at: number
  anonymousId: string
  userId: string | null
  /** An identify's traits, as analytics.js merges them. */
  traits?: Record<string, unknown>
  page: BufferedPage
  /** What the source middleware puts over every call's properties: the defaults, then the email. */
  sourceDefaults: Record<string, unknown>
  timeZone?: string
  owner: DeliveryOwner
}

/**
 * The event Segment receives for a call, built when the call is made: a page call's properties
 * gain the page context and its name before the source middleware runs, an identify gets the
 * middleware's properties too, and `context.page` is the page's own except where a page call's
 * properties name it. Copied as JSON, so a payload changed later changes nothing here.
 */
export function cdpCall(call: SegmentCall, moment: CallMoment): CdpCall {
  const page = pageContextOf(moment.page)
  let properties: Record<string, unknown>
  let contextPage: PageContext = page
  if (call.type === 'page') {
    properties = { ...page, ...call.properties, ...(call.name ? { name: call.name } : {}) }
    contextPage = { ...page, ...pick(call.properties, PAGE_CONTEXT_KEYS) }
  } else {
    properties = call.type === 'track' ? { ...call.properties } : {}
  }
  properties = { ...properties, ...moment.sourceDefaults }

  const query = typeof contextPage.search === 'string' ? contextPage.search : ''
  const copy: CdpCall = {
    type: call.type,
    ...(call.type === 'track' ? { event: call.event } : {}),
    ...(call.type === 'page' && call.name ? { name: call.name } : {}),
    properties,
    ...(call.type === 'identify' ? { traits: moment.traits ?? call.traits } : {}),
    messageId: moment.messageId,
    anonymousId: moment.anonymousId,
    ...(moment.userId ? { userId: moment.userId } : {}),
    originalTimestamp: new Date(moment.at).toISOString(),
    context: {
      page: contextPage,
      ...(query ? { campaign: campaignOf(query) } : {}),
      ...(moment.timeZone ? { timezone: moment.timeZone } : {}),
      deliveryOwner: moment.owner,
    },
  }
  return JSON.parse(JSON.stringify(copy)) as CdpCall
}

function pick(
  source: Record<string, unknown>,
  keys: readonly (keyof PageContext)[],
): Partial<PageContext> {
  const picked: Record<string, unknown> = {}
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) picked[key] = source[key]
  }
  return picked as Partial<PageContext>
}

/** analytics.js's messageId, so Klaviyo's `unique_id` keeps its shape whoever mints it. */
export function messageIdOf(now: number, uuid: () => string): string {
  return `ajs-next-${now}-${uuid()}`
}

export function randomUuid(): string {
  const random = (globalThis as { crypto?: Crypto }).crypto
  if (typeof random?.randomUUID === 'function') return random.randomUUID()
  const bytes = new Uint8Array(16)
  if (typeof random?.getRandomValues === 'function') random.getRandomValues(bytes)
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256)
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function pageTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

// ---------------------------------------------------------------------------
// The client for the copies

/**
 * A copy's own event fields, handed to the client with the call. Each `context` key given replaces
 * the client's whole value and one given as undefined is left out, so every key analytics.js
 * decides goes as analytics.js built it: `page` whole, `campaign` and `timezone` also where it has
 * none, or the client's own from the page at send time would stay. The client still adds the user
 * agent, its hints and the locale, as analytics.js does, and always names itself in `library`.
 */
export function copyOptions(copy: CdpCall): CallOptions {
  return {
    messageId: copy.messageId,
    anonymousId: copy.anonymousId,
    userId: copy.userId,
    originalTimestamp: copy.originalTimestamp,
    context: {
      page: copy.context.page,
      campaign: copy.context.campaign,
      timezone: copy.context.timezone,
      deliveryOwner: copy.context.deliveryOwner,
    },
  }
}

/** One copy, made as the call Segment receives: an identify's properties go in its options. */
export function sendCopy(client: Analytics, copy: CdpCall): Promise<void> {
  const options = copyOptions(copy)
  if (copy.type === 'track') return client.track(copy.event!, copy.properties, options)
  if (copy.type === 'page') return client.page(copy.name, copy.properties, options)
  return client.identify(copy.userId, copy.traits, { ...options, properties: copy.properties })
}

/**
 * Where a page hands its copies: the client opened at the first, and each copy given to it only
 * once the one before is in its queue. The client queues an identify a step later than a call
 * made right after it, and Segment receives them in the order they were made. Never awaited.
 */
export function copySender(open: () => Promise<Analytics>): (copy: CdpCall) => void {
  let client: Promise<Analytics | null> | null = null
  let previous: Promise<void> = Promise.resolve()
  return (copy) => {
    client ??= open().catch((err: unknown) => {
      console.warn(
        '[Segment] CDP client failed to load; no calls reach the CDP from this page',
        err,
      )
      return null
    })
    const loading = client
    previous = previous.then(async () => {
      const loaded = await loading
      try {
        if (loaded) await sendCopy(loaded, copy)
      } catch {
        // The client reports a call it could not take; the next copy still goes.
      }
    })
  }
}

/** Test seams: a page passes none of these and gets the browser's own. */
export type ClientPorts = Pick<
  NonNullable<Parameters<typeof Analytics.load>[1]>,
  'window' | 'httpPost' | 'httpGet' | 'timer' | 'clock' | 'uuid'
>

/**
 * The client the copies go through: the CDP source imported from this Segment source. It keeps no
 * visitor, so it never reads or moves the one the funnel's client or analytics.js keeps, and no
 * Amplitude session, because Segment's is off. No high-entropy client hints, as analytics.js is
 * loaded without them.
 */
export async function openCopyClient(
  settings: { writeKey: string; host: string },
  ports: ClientPorts = {},
): Promise<Analytics> {
  const { load } = await import('@cppv3/cdp-browser')
  return load(settings, { identity: 'caller', amplitudeSession: false, ...ports })
}
